/**
 * Product deletion and duplicate merging.
 *
 * Six tables reference products with an ON DELETE NO ACTION foreign key, so
 * Postgres refuses to delete any product carrying historical records. Rather
 * than erasing that history to force the delete through, we check first and
 * explain the refusal, offering a merge into the canonical product as the way
 * to resolve genuine duplicates.
 */

import { supabase } from './supabase'
import { authenticatedFetch } from '../hooks/useAuthenticatedFetch'
import { createContextLogger } from './logger'

const log = createContextLogger('productDeletion')

export interface DeletionBlocker {
  table: BlockerTable
  label: string
  count: number
}

export interface ProductDeletionCheck {
  canDelete: boolean
  blockers: DeletionBlocker[]
  /** Ready-to-display explanation, empty when the product can be deleted. */
  message: string
}

export interface MergeProductResult {
  success: boolean
  error?: string
  movedSalesRecords?: number
  movedHistoricalRows?: number
}

/**
 * Reports what prevents a product from being deleted. A failure to run the
 * check is reported as "cannot delete" rather than assumed safe, so an outage
 * can never lead to a destructive delete attempt.
 */
export async function checkProductDeletion({
  productId,
  adminId,
}: {
  productId: string
  adminId: string
}): Promise<ProductDeletionCheck> {
  const { data, error } = await supabase.rpc('get_product_deletion_blockers', {
    p_admin_id: adminId,
    p_product_id: productId,
  })

  if (error) {
    log.error('Deletion pre-flight check failed', { error: error.message })
    return {
      canDelete: false,
      blockers: [],
      message: 'Could not check whether this product is safe to delete. Please try again.',
    }
  }

  const counts = (data ?? {}) as Partial<Record<BlockerTable, number>>
  const blockers = BLOCKER_TABLES
    .map(table => ({
      table,
      label: BLOCKER_LABELS[table].plural,
      count: Number(counts[table] ?? 0),
    }))
    .filter(blocker => blocker.count > 0)

  if (blockers.length === 0) return { canDelete: true, blockers: [], message: '' }

  return { canDelete: false, blockers, message: describeBlockers(blockers) }
}

/**
 * Merges a duplicate into the product being kept, moving all of its history.
 * Runs server-side through the product-merge function so the tenant is derived
 * from a verified token and the whole move is one transaction.
 */
export async function mergeProductInto({
  sourceProductId,
  targetProductId,
}: {
  sourceProductId: string
  targetProductId: string
}): Promise<MergeProductResult> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

  if (!supabaseUrl) return { success: false, error: 'Supabase configuration not found' }

  const { data, error } = await authenticatedFetch<MergeResponse>(
    `${supabaseUrl}/functions/v1/product-merge`,
    { method: 'POST', body: JSON.stringify({ sourceProductId, targetProductId }) },
  )

  if (error || !data?.success) {
    log.warn('Product merge rejected', { error: error ?? 'unknown' })
    return { success: false, error: error ?? 'Failed to merge product' }
  }

  const moved = data.summary?.moved
  const movedSalesRecords = moved?.shop_sales_history ?? 0
  const movedHistoricalRows = moved
    ? Object.values(moved).reduce((total, count) => total + count, 0) - movedSalesRecords
    : 0

  log.info('Product merged', { movedSalesRecords, movedHistoricalRows })

  return { success: true, movedSalesRecords, movedHistoricalRows }
}

function describeBlockers(blockers: DeletionBlocker[]): string {
  const parts = blockers.map(({ table, count }) => {
    const { singular, plural } = BLOCKER_LABELS[table]
    return `${count} ${count === 1 ? singular : plural}`
  })

  const listed = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`

  return `This product can't be deleted because it still has ${listed} on record. `
    + 'Deleting it would remove that history. If it is a duplicate, merge it into the '
    + 'product you are keeping instead — the history moves across and nothing is lost.'
}

interface MergeResponse {
  success?: boolean
  summary?: { moved?: Record<BlockerTable, number> }
}

type BlockerTable =
  | 'shop_sales_history'
  | 'sale_items'
  | 'invoice_items'
  | 'reservation_items'
  | 'shipment_items'
  | 'outgoing_shipment_items'

const BLOCKER_TABLES: BlockerTable[] = [
  'shop_sales_history',
  'sale_items',
  'invoice_items',
  'reservation_items',
  'shipment_items',
  'outgoing_shipment_items',
]

const BLOCKER_LABELS: Record<BlockerTable, { singular: string; plural: string }> = {
  shop_sales_history: { singular: 'Shopify sale', plural: 'Shopify sales' },
  sale_items: { singular: 'in-store sale', plural: 'in-store sales' },
  invoice_items: { singular: 'invoice line', plural: 'invoice lines' },
  reservation_items: { singular: 'reservation', plural: 'reservations' },
  shipment_items: { singular: 'incoming shipment', plural: 'incoming shipments' },
  outgoing_shipment_items: { singular: 'outgoing shipment', plural: 'outgoing shipments' },
}
